import "./media-asset-row.scss";

import cn from "clsx";
import {
  ComponentHeader,
  ComponentHeaderProps,
} from "design-system/components/primitives/component-header/component-header";
import {
  MediaAsset,
  MediaAssetProps,
} from "design-system/components/primitives/media-asset/media-asset";
import { Alignment, EditAttributes } from "design-system/types/types";
import { ReactNode } from "react";

export interface MediaAssetRowProps {
  assets?: Array<MediaAssetProps>;
  align?: Alignment;
  header?: ComponentHeaderProps;
  caption?: ReactNode;
  HeadingLevel?: "h2" | "h3";
  isVerticalImages?: boolean;

  editAttributes?: {
    caption?: EditAttributes;
  };
}

/**
 * For displaying media assets in a responsive row. Allows a maximum of 3 assets
 *
 * ## See it in use on...
 * - An [article page](/story/example-pages-article-pages-big-art-topper--story)
 *
 * - **`id: CB-009-000-00`**
 * - **`data-region: cb__media-asset-row`**
 */
export function MediaAssetRow({
  assets,
  align = "wide",
  caption,
  header,
  HeadingLevel = "h2",
  editAttributes,
  isVerticalImages,
}: MediaAssetRowProps) {
  if (!Array.isArray(assets)) return null;

  // Allow maximum 3 assets
  let assetsToDisplay = assets;
  if (assets.length > 3) {
    assetsToDisplay = assets.slice(0, 3);
  }

  const count = assetsToDisplay.length;

  const wrapperClass = cn(
    `hbs-global-align-${align}`,
    "hbs-component--media-asset-row",
  );

  return (
    <div className={wrapperClass} data-region="cb__media-asset-row">
      {header && <ComponentHeader {...header} HeadingLevel={HeadingLevel} />}

      <div className={`hbs-media-asset-row hbs-media-asset-row--${count}`}>
        {assetsToDisplay.map((asset: MediaAssetProps, i: number) => (
          <div
            className={cn(
              "hbs-media-asset-row__item",
              isVerticalImages && "hbs-media-asset-row__item--vertical",
            )}
            key={i}
          >
            <MediaAsset {...asset} />
          </div>
        ))}
      </div>

      {caption && (
        <div
          {...editAttributes?.caption}
          className="hbs-media-asset-row__caption"
        >
          {caption}
        </div>
      )}
    </div>
  );
}
